import { getLocaleText } from "shared/boot/i18n";
import ExternalItemsService from "shared/services/api/ExternalItemsService";
import { type Maybe, type Nullable } from "shared/types";

import { type UrlData } from "../shared/types";

import useUrlVerificationNotificationsSubscription, {
  type UrlVerificationResult,
} from "./useUrlVerificationNotificationsSubscription";

const SINGLE_URL_RESULT_MAX_WAITING_TIME = 30_000;

export default function useUrlsApiVerification() {
  let unsubscribeFromVerificationNotifications: Maybe<() => void>;

  function verifyUrlsWithApi(urlsToVerifyData: UrlData[]) {
    const { subscribeToNotifications, unsubscribeFromNotifications } =
      useUrlVerificationNotificationsSubscription();

    unsubscribeFromVerificationNotifications = unsubscribeFromNotifications;

    return new Promise<void>(
      (resolveVerificationPromise, rejectVerificationPromise) => {
        const handleError = (error: Error) => {
          cleanupSubscription();
          rejectVerificationPromise(error);
        };

        const subscriptionTimeout = useSingleUrlResultTimeout(() =>
          handleError(noVerificationResultsError())
        );

        const updateUrlData = getUrlDataUpdater(urlsToVerifyData, () => {
          unsubscribeFromNotifications();
          resolveVerificationPromise();
          subscriptionTimeout.clear();
        });

        subscribeToNotifications(
          (verificationResult: UrlVerificationResult) => {
            updateUrlData(verificationResult);
            subscriptionTimeout.reset();
          }
        )
          .then(() => bulkVerifyUrls(urlsToVerifyData))
          .then(() => subscriptionTimeout.set())
          .catch((error: Error) => {
            subscriptionTimeout.clear();
            handleError(error);
          });
      }
    );
  }

  function getUrlDataUpdater(
    urlsToVerifyData: UrlData[],
    onAllUpdated: () => void
  ) {
    const urlsToVerifyDataByUrl = new Map(
      urlsToVerifyData.map((urlData) => [urlData.url, urlData])
    );

    return (verificationResult: UrlVerificationResult) => {
      const urlData = urlsToVerifyDataByUrl.get(verificationResult.url);

      if (!urlData) return;

      // modifying the original objects to simplify the code (avoid passing
      // callbacks along) as we want to instantly see each url update in the UI

      urlData.status = verificationResult.url_status;
      urlData.mentionType = verificationResult.mention_type;
      urlData.mentionId = verificationResult.mention_id;
      urlData.reason = verificationResult.reason;

      urlsToVerifyDataByUrl.delete(verificationResult.url);

      if (urlsToVerifyDataByUrl.size === 0) {
        onAllUpdated();
      }
    };
  }

  function bulkVerifyUrls(urlsToVerifyData: UrlData[]): Promise<any> {
    return ExternalItemsService.bulkVerifyUrls(
      urlsToVerifyData.map((urlData) => urlData.url)
    );
  }

  function cancelUrlsWithApiVerification() {
    unsubscribeFromVerificationNotifications?.();
  }

  function cleanupSubscription() {
    try {
      // best effort - try to unsubscribe, but ignore if failure happens
      unsubscribeFromVerificationNotifications?.();
    } finally {
      unsubscribeFromVerificationNotifications = undefined;
    }
  }

  function useSingleUrlResultTimeout(onTimeout: () => void) {
    let timeout: Nullable<ReturnType<typeof setTimeout>> = null;

    const set = () => {
      timeout = setTimeout(onTimeout, SINGLE_URL_RESULT_MAX_WAITING_TIME);
    };

    const clear = () => {
      if (timeout) clearTimeout(timeout);
    };

    const reset = () => {
      clear();
      set();
    };

    return { clear, reset, set };
  }

  function noVerificationResultsError() {
    return new Error(
      getLocaleText("external_items_bulk_add_modal.no_verification_results")
    );
  }

  return {
    cancelUrlsWithApiVerification,
    verifyUrlsWithApi,
  };
}
